import Vue from 'vue'
import Router from 'vue-router'
import auth from '@/libs/auth'
import socket from '@/libs/socket'
import userAgent from '@/libs/user-agent'
import store from '@/store'

// pages
const AcceptInvite = () => import(/* webpackChunkName: "PageAcceptTeamInvite" */ '@/components/Pages/AcceptTeamInvite')
const AppRedirect = () => import(/* webpackChunkName: "PageAppRedirect" */ '@/components/Pages/AppRedirect')
const Contact = () => import(/* webpackChunkName: "PageContact" */ '@/components/Pages/Contact')
const Legal = () => import(/* webpackChunkName: "PageLegal" */ '@/components/Pages/Legal')
const Login = () => import(/* webpackChunkName: "ModalLogin" */ '@/components/Modals/Login')
const PhoneLogin = () => import(/* webpackChunkName: "PhoneLogin" */ '@/components/Forms/PhoneLogin')
const ResetPassword = () => import(/* webpackChunkName: "PageResetPassword" */ '@/components/Pages/ResetPassword')
const LegacySearch = () => import(/* webpackChunkName: "PageSearchResults" */ '@/components/Pages/SearchResults')
const Search = () => import(/* webpackChunkName: "PageSearchResults" */ '@/components/Pages/Search')
const LeaderBoard = () => import(/* webpackChunkName: "PageLeaderBoard" */ '@/components/Pages/LeaderBoard')
const ProjectLeaderBoard = () => import(/* webpackChunkName: "PageLeaderBoard" */ '@/components/Pages/ProjectLeaderBoard')
const Maintenance = () => import(/* webpackChunkName: "PageMaintenance" */ '@/components/Pages/Maintenance')
const ViewAll = () => import(/* webpackChunkName: "PageViewAll" */ '@/components/Pages/ViewAll')
const Resources = () => import(/* webpackChunkName: "PageResources" */ '@/components/Pages/Resources')
const Cookies = () => import(/* webpackChunkName: "PageCookies" */ '@/components/Pages/Cookies')
const UpdateFeed = () => import(/* webpackChunkName: "PageUpdateFeed" */ '@/components/Pages/UpdateFeed')
const VirtualLobby = () => import(/* webpackChunkName: "VirtualLobby" */ '@/components/Pages/Lobby')
const AdminJudge = () => import(/* webpackChunkName: "AdminJudge" */ '@/components/Pages/AdminJudge')

// iframe pages
const WixUrl = () => import(/* webpackChunkName: "WixUrl" */ '@/components/Pages/IframeUrl')

// sections
const Admin = () => import(/* webpackChunkName: "SectionAdmin" */ '@/components/Admin/SectionAdmin')
// re-enable when the banner feature is complete
// const ManageBanners = () => import(/* webpackChunkName: "ManageBanners" */ '@/components/Admin/SectionAdmin/Banners/ManageRegionBanners')
const ManageIFrames = () => import(/* webpackChunkName: "ManageIframes" */ '@/components/Admin/SectionAdmin/Boards/Iframe')
const Home = () => import(/* webpackChunkName: "SectionHome" */ '@/components/Base/SectionHome')
const Messaging = () => import(/* webpackChunkName: "Messaging" */ '@/components/Pages/Messaging')
const Profile = () => import(/* webpackChunkName: "Profile" */ '@/components/Pages/Profile')
const ProjectView = () => import(/* webpackChunkName: "SectionProjectView" */ '@/components/SectionProjectView/Project')
const CreateEdit = () => import(/* webpackChunkName: "SectionCreateEdit" */ '@/components/Base/SectionCreateEdit')
const LobbyCreate = () => import(/* webpackChunkName: "Create" */ '@/components/Admin/SectionAdmin/ClickableAreas/Create')
const LobbyView = () => import(/* webpackChunkName: "View" */ '@/components/Admin/SectionAdmin/ClickableAreas/View')

// misc.
const NotFound = () => import(/* webpackChunkName: "PageNotFound" */ '@/components/Pages/NotFound')
const ProjectBuildTemplate = () => import(/* webpackChunkName: "miscBuildTemplate" */ '@/components/SectionProjectView/BuildTemplate')
const BlankPage = () => import(/* webpackChunkName: "miscBuildTemplate" */ '@/components/Pages/Blank')
const BlockedAccount = () => import(/* webpackChunkName: "BlockedAccount" */ '@/components/Pages/BlockedAccount')

const CategoryByYear = () => import(/* webpackChunkName: "CategoryByYear" */ '@/components/Pages/CategoryByYear')

Vue.use(Router)

const getRedirect = () => {
  switch (process.env.APP_NAME) {
    // We show the landing page if the APP is one of the below. Thus, redirect is undefined.
    case 'Ysc':
    case 'Ajas':
    case 'Isef':
    case 'Girls-and-steam':
    case 'Projectboard':
    case 'Societyforsciencebcm':
    case 'Ohiosci':
    case 'Cnmiiwsf':
    case 'Sts':
    case 'Buckeye':
    case 'Oasam':
      return undefined
    // We redirect to the home page (showing projects) if APP isn't one of the above
    default:
      return { name: 'home' }
  }
}

const getComponent = () => {
  // Return the respective landing pages. For other partners, we redirect to home, hence the component
  // is undefined.
  switch (process.env.APP_NAME) {
    case 'Ysc':
      return () => import(/* webpackChunkName: "YSC" */'@/components/Pages/LandingPage/YSC')
    case 'Ajas':
      return () => import(/* webpackChunkName: "AJAS" */'@/components/Pages/LandingPage/AJAS')
    case 'Isef':
      return () => import(/* webpackChunkName: "ISEF" */'@/components/Pages/LandingPage/ISEF')
    case 'Projectboard':
    case 'Girls-and-steam':
    case 'Ohiosci':
      return WixUrl
    case 'Sts':
      return () => import(/* webpackChunkName: "PageSTS" */ '@/components/Pages/LandingPage/STS')
    case 'Societyforsciencebcm':
      return () => import(/* webpackChunkName: "Broadcom" */'@/components/Pages/LandingPage/Broadcom')
    case 'Cnmiiwsf':
      return () => import(/* webpackChunkName: "GasLobby" */ '@/components/Pages/Lobby').then(({ Lobby }) => Lobby(process.env.APP_NAME))
    case 'Buckeye':
      return () => import(/* webpackChunkName: "BuckeyeLobby" */ '@/components/Pages/LandingPage/BuckeyeLobby')
    case 'Oasam':
      return () => import(/* webpackChunkName: "Oasam" */ '@/components/Pages/LandingPage/Oasam')
    case 'Sigmaxi':
      return () => import(/* webpackChunkName: "Broadcom" */'@/components/Pages/LandingPage/Sigmaxi')
    default:
      return undefined
  }
}

const getPartnerRoutes = () => {
  let partnerRoutes = []
  if (process.env.APP_NAME === 'Make') {
    partnerRoutes = require('./make')
  }
  if (process.env.APP_NAME === 'Societyforsciencebcm') {
    partnerRoutes = require('./broadcom')
  }
  if (process.env.APP_NAME === 'Girls-and-steam') {
    partnerRoutes = require('./girls-and-steam')
  }
  if (process.env.APP_NAME === 'Heise') {
    partnerRoutes = require('./heise')
  }
  if (process.env.APP_NAME === 'Isef') {
    partnerRoutes = require('./isef')
  }
  if (process.env.APP_NAME === 'Neweng') {
    partnerRoutes = require('./neweng')
  }
  if (process.env.APP_NAME === 'Projectboard') {
    partnerRoutes = require('./pbworld')
  }
  if (process.env.APP_NAME === 'Ysc') {
    partnerRoutes = require('./ysc')
  }
  if (process.env.APP_NAME === 'Sts') {
    partnerRoutes = require('./sts')
  }
  if (process.env.APP_NAME === 'Ohiosci') {
    partnerRoutes = require('./oas')
  }
  if (process.env.APP_NAME === 'Oasam') {
    partnerRoutes = require('./oasam')
  }
  if (process.env.APP_NAME === 'Sigmaxi') {
    partnerRoutes = require('./sigmaxi')
  }

  return partnerRoutes
}

const router = new Router({
  base: process.env.BASE,
  mode: userAgent.isApp() ? 'hash' : 'history',
  scrollBehavior: function (to, from, savedPosition) {
    if (savedPosition) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          resolve(savedPosition)
        }, 2000)
      })
    } else if (to.hash) {
      return { selector: to.hash }
    } else {
      return { x: 0, y: 0 }
    }
  },
  routes: [
    {
      // This is two routes in one. For some partner sites, we direct the user from / to /home
      // For others, we keep the user on / and show the respective landing page.
      path: '/',
      name: 'landingRedirect',
      meta: { requiresAuth: false },
      redirect: getRedirect(),
      // Since we are redirecting when APP isn't YSC, there is no component to show for that route. Thus,
      // it is undefined. When APP is YSC, we show the YSC (landing page) component instead.
      component: getComponent()
    },
    {
      path: '/:partner?/home',
      name: 'home',
      component: Home,
      meta: { requiresAuth: false },
      beforeEnter: (to, from, next) => {
        switch (process.env.APP_NAME) {
          case 'Ohiosci':
          case 'Oasam':
          case 'Ysc':
            next({ name: 'exploreFair', params: { fairSlug: 'home' } })
            break
          case 'Girls-and-steam':
          case 'Societyforsciencebcm':
            next({ name: 'landingRedirect' })
            break
          case 'Sts':
            next({ path: '/' })
            break
          default:
            next()
            break
        }
      }
    },
    {
      path: '*/index.html',
      name: 'homeApp',
      component: Home,
      meta: { requiresAuth: false }
    },
    {
      path: '/:partner?/loginPage',
      name: 'login',
      component: Login,
      meta: { engComNoAuth: true },
      beforeEnter: (to, from, next) => {
        if (auth.isLoggedIn() && !to.query.redirect) {
          next({ name: 'home' })
        } else {
          next()
        }
      }
    },
    {
      path: '/:partner?/login/sms/:view?',
      name: 'loginSMS',
      component: PhoneLogin,
      meta: { hideFooter: true, hideMobileNav: true },
      beforeEnter: (to, from, next) => {
        if (auth.isLoggedIn()) {
          next({ name: 'home' })
        } else {
          next()
        }
      }
    },
    {
      path: '/:partner?/logout',
      name: 'logout',
      beforeEnter: (to, from, next) => {
        auth.logout().then(() => {
          const key = (Math.random() + 1).toString(36).substring(7)
          if (process.env.APP_NAME === 'Ysc' || process.env.APP_NAME === 'Isef') {
            next({ name: 'landingRedirect', params: { logout: true } })
          } else {
            next({ name: 'home', query: { r: key }, params: { logout: true } })
          }
        })
      }
    },
    {
      path: '/:partner?/password/reset',
      name: 'resetPassword',
      component: ResetPassword,
      meta: { engComNoAuth: true }
    },
    {
      path: '/:partner?/project/:id',
      name: 'viewProject',
      component: ProjectView,
      meta: { hideFooter: false }
    },
    {
      path: '/:partner?/tag/:tag',
      name: 'projectsTagged',
      component: Home,
      meta: { requiresAuth: false }
    },
    {
      path: '/:partner?/category/:category',
      name: 'projectsCategory',
      component: Home,
      meta: { requiresAuth: false }
    },
    {
      path: '/:partner?/explore/:explorepage',
      name: 'explore-page',
      alias: '/explore-page',
      component: Home
    },
    {
      path: '/:partner?/discover/:discoverpage',
      name: 'discover',
      alias: '/discover',
      component: ViewAll
    },
    {
      path: '/:partner?/create/:id?',
      name: 'create',
      component: CreateEdit,
      meta: { hideFooter: true, requiresAdmin: true },
      alias: '/story/:id/edit'
    },
    {
      path: '/:partner?/contact',
      name: 'contact',
      component: Contact,
      meta: { requiresAuth: false }
    },
    {
      path: '/:partner?/legal',
      name: 'legal',
      component: Legal
    },
    {
      path: '/:partner?/terms-and-conditions',
      name: 'terms',
      component: WixUrl,
      meta: { fixedToolbar: true }
    },
    {
      path: '/:partner?/privacy-policy',
      name: 'privacy',
      component: WixUrl,
      meta: { fixedToolbar: true }
    },
    {
      path: '/:partner?/leaderboard',
      name: 'leaderboard',
      component: LeaderBoard
    },
    {
      path: '/:partner?/ranking',
      name: 'ranking',
      component: ProjectLeaderBoard
    },
    {
      path: '/:partner?/legacy-search-results/:searchQuery',
      name: 'legacySearch',
      component: LegacySearch,
      meta: { requiresAuth: false }
    },
    {
      path: '/:partner?/search-results/:searchQuery?',
      name: 'search',
      component: Search,
      meta: { requiresAuth: false }
    },
    {
      path: '/:partner?/profile/:username?/:tab?',
      name: 'profile',
      component: Profile
    },
    {
      path: '/:partner?/inbox/:username?',
      name: 'messaging',
      component: Messaging,
      meta: { hideFooter: true }
    },
    {
      path: '/:partner?/inbox/project/:projectID',
      name: 'projectMessaging',
      component: Messaging,
      meta: { hideFooter: true }
    },
    {
      path: '/:partner?/inbox/public/:topic',
      name: 'publicMessaging',
      component: Messaging,
      meta: { hideFooter: true }
    },
    {
      path: '/:partner?/inbox/group/:topic',
      name: 'groupMessaging',
      component: Messaging,
      meta: { hideFooter: true }
    },
    {
      path: '/:partner?/admin',
      name: 'admin',
      component: Admin,
      meta: { requiresAuth: true }
    },
    {
      path: '/:partner?/admin/:section/:name',
      name: 'adminWithPath',
      component: Admin,
      meta: { requiresAuth: true }
    },
    {
      path: '/:partner?/admin/clickableAreas/create',
      name: 'lobbyCreate',
      component: LobbyCreate,
      meta: { requiresAuth: true, requiresAdmin: true },
      props: true
    },
    {
      path: '/:partner?/admin/clickableAreas/View',
      name: 'lobbyView',
      component: LobbyView,
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    //  re-enable when the banner feature is complete
    // {
    //   path: '/:partner?/contests/banners',
    //   name: 'manageBanners',
    //   component: ManageBanners,
    //   meta: { requiresAuth: true, requiresAdmin: false }
    // },
    {
      path: '/:partner?/contests/iframes',
      name: 'manageIFrames',
      component: ManageIFrames,
      meta: { requiresAuth: true, requiresAdmin: false }
    },
    {
      path: '/:partner?/su/exit',
      name: 'suExit',
      beforeEnter: (to, from, next) => {
        auth.exitSu()
        next(false)
        const base = process.env.BASE
        if (base === undefined || base === '/') {
          window.location = from.path
        } else {
          window.location = `${base.substr(0, base.length - 1)}${from.path}`
        }
      }
    },
    {
      path: '/:partner?/join',
      name: 'acceptInvite',
      component: AcceptInvite
    },
    {
      path: '/:partner?/signup',
      name: 'signUp',
      beforeEnter: (to, from, next) => {
        next({ name: 'home' })
      }
    },
    {
      path: '/:partner?/startButtonTemplate',
      name: 'startButtonTemplate',
      redirect: {
        path: '/:partner?/start/template',
        query: { projectID: 21744, startButton: true, draft: true, private: true }
      },
      meta: { requiresAuth: true, requiresAdmin: false }
    },
    {
      path: '/:partner?/start/:template',
      name: 'StartWithTemplate',
      component: ProjectBuildTemplate,
      meta: { hideFooter: true, hideMobileNav: true },
      beforeEnter: (to, from, next) => {
        if (auth.isLoggedIn()) {
          next()
        } else {
          next({ name: 'blankPage', query: to.query, params: to.params })
        }
      }
    },
    {
      path: '/:partner?/blankpage',
      name: 'blankPage',
      component: BlankPage
    },
    {
      path: '/:partner?/redirect',
      name: 'AppRedirect',
      component: AppRedirect,
      meta: { requiresAuth: false, hideFooter: true, hideMobileNav: true }
    },
    {
      path: '/:partner?/maintenance',
      name: 'Maintenance',
      component: Maintenance,
      meta: { requiresAuth: false, hideNavbar: true, hideFooter: true }
    },
    {
      path: '/feed',
      name: 'UpdateFeed',
      component: UpdateFeed,
      beforeEnter: (to, from, next) => {
        if (!auth.isLoggedIn()) {
          next({ name: 'home' })
        } else {
          next()
        }
      }
    },
    {
      path: '/:partner?/about',
      name: 'about',
      component: WixUrl,
      meta: { fixedToolbar: true }
    },
    {
      path: '/:partner?/registration',
      name: 'registration',
      component: WixUrl
    },
    {
      path: '/:partner?/finalists',
      name: 'isefFinalists',
      component: WixUrl
    },
    {
      path: '/:partner?/viewAll',
      name: 'viewAll',
      component: ViewAll,
      beforeEnter: (to, from, next) => {
        if (['Ohiosci', 'Oasam', 'Ysc'].includes(process.env.APP_NAME) && to.query.category) {
          store.dispatch('getFairForCategory', to.query.category).then(() => {
            store.dispatch('setPage', store.getters.fairForCategory(to.query.category).explore_path)
            next()
          })
        } else {
          next()
        }
      }
    },
    {
      path: '/:partner?/adminJudge',
      name: 'adminJudge',
      component: AdminJudge
    },
    {
      path: '/:partner?/auditorium/:auditoriumSlug',
      name: 'auditoriumType',
      component: Resources,
      meta: { requiresAuth: false }
    },
    {
      path: '/:partner?/resources',
      name: 'resources',
      component: process.env.APP_NAME === 'Ohiosci' ? WixUrl : Resources
    },
    {
      path: '/:partner?/embed',
      name: 'embed',
      component: WixUrl,
      meta: { requiresAuth: false, hideFooter: true, hideMobileNav: true, hideNavbar: true }
    },
    {
      path: '/makershed',
      redirect: { path: '/profile/makershed' }
    },
    {
      path: '/:partner?/cookies',
      name: 'cookies',
      component: Cookies
    },
    {
      path: '/:partner?/oster-challenge',
      redirect: {
        path: '/:partner?/oster-contest'
      }
    },
    {
      path: '/:partner?/2d/:path',
      name: 'virtuallobby',
      component: VirtualLobby
    },
    {
      path: '/:partner?/notFound',
      name: 'notFound',
      component: NotFound
    },
    {
      path: '/:partner?/partners',
      name: 'partners',
      component: WixUrl
    },
    {
      path: '/:partner?/sponsors',
      name: 'sponsors',
      component: WixUrl
    },
    {
      path: '/:partner?/special-award-organizations',
      name: 'special-award-organizations',
      component: WixUrl
    },
    {
      path: '/makeprojects-home',
      beforeEnter () { location.href = 'https://www.makeprojects.com' },
      name: 'landingMake'
    },
    {
      path: '/projectboard-ysc',
      beforeEnter () { location.href = 'https://projectboard.world/ysc' },
      name: 'landingYsc'
    },
    {
      path: '/projectboard-isef',
      beforeEnter () { location.href = 'https://projectboard.world/isef' },
      name: 'landingIsef'
    },
    {
      path: '/makeprojects-edu',
      beforeEnter () { location.href = 'https://makeprojects.com/category/education' },
      name: 'landingEdu'
    },
    {
      path: '/stemevents',
      name: 'landingSteam',
      component: WixUrl
    },
    {
      path: '/schools',
      name: 'landingSchool',
      component: WixUrl
    },
    {
      path: '/engineering-home',
      beforeEnter () { location.href = 'https://new.engineering.com' },
      name: 'landingEngineering'
    },
    {
      path: '/about',
      name: 'landingAbout',
      component: WixUrl
    },
    {
      path: '/engineering-legal',
      beforeEnter () { location.href = 'https://new.engineering.com/legal' },
      name: 'landingLegal'
    },
    {
      path: '/coming-soon',
      name: 'comingSoon',
      component: WixUrl
    },
    {
      path: '/:partner?/blocked-account',
      name: 'blockedAccount',
      component: BlockedAccount
    },
    {
      path: '/explore/categories/:year',
      name: 'categoryByYear',
      component: CategoryByYear
    },
    ...getPartnerRoutes(),
    /*
      This should be the last route, routes are matched in order
      This catchall will prevent routing to anything below this point
    */
    {
      path: '*',
      component: WixUrl
    }
  ]
})

router.beforeEach((to, from, next) => {
  if (to.name === 'comingSoon' && Vue.prototype.$isBroadcomMasters) {
    next({ name: 'landingRedirect' })
  }

  if (to.name === 'exploreResources' && !Vue.prototype.$isEngCom) {
    to.matched[0].components.default = NotFound
    return next()
  }
  const requiresAuth = to.matched.some(record => !!record.meta.requiresAuth)
  const requiresAdmin = to.matched.some(record => !!record.meta.requiresAdmin)
  if (to.query && to.query.utm_source) {
    auth.setUtmSource(to.query.utm_source)
  }

  if (to.query && to.query.rc) {
    auth.setReferralCode(to.query.rc)
  }

  auth.checkLogin().then(() => {
    if (requiresAuth) {
      if (auth.isLoggedIn()) {
        if (!requiresAdmin) {
          return next()
        }
      } else {
        return next({ name: 'home' })
      }
    }
    if (requiresAdmin) {
      if (auth.isAdmin() || auth.isAnalytic() || auth.isCurator() || auth.isResourceEditor()) {
        return next()
      } else {
        // manually set the component to NotFound
        // this way it appears like a normal 404
        // (i.e. path is maintained)
        to.matched[0].components.default = NotFound
        return next()
      }
    }
    return next()
  }, err => console.error(err))
})

router.afterEach((to, from) => {
  socket.log(from.fullPath, to.fullPath)
})

export default router
